import { Image as ImageIcon } from "@phosphor-icons/react"
import { Checkbox, Tooltip } from "@vesatogo/grass-core"
import dayjs from "dayjs"
import { useState } from "react"
import AttachmentModal from "~/components/AttachmentModal"
import TradeKindTag from "~/components/TradeKindTag"

export const HistoryColumns = {
  DATE: { label: "Date of Payment", accessor: "date" },
  ACCOUNT_HOLDER_NAME: {
    label: "Account Holder Name",
    accessor: "account_holder_name",
  },
  ACCOUNT_NO: { label: "Account number", accessor: "account_no" },
  PAYMENT_MODE: { label: "Payment Mode", accessor: "payment_mode" },
  TRANSACTION_ID: {
    label: "Transaction ID",
    accessor: "transaction_id",
  },
  RELEASED_AMOUNT: { label: "Amount", accessor: "released_amount" },
  PATTI_NO: { label: "Patti no.", accessor: "patti_no" },
  PATTI_TYPE: { label: "Patti Type", accessor: "kind" },
  ADVANCE_TYPE: { label: "Advance Type", accessor: "advance_type" },
  PAYMENT_OPT: { label: "Payment Option", accessor: "payment_option" },
  ADVANCE_AMOUNT: { label: "Advance Amount", accessor: "advance_amount" },
  PARENT_TRANSACTION_ID: {
    label: "Settled From",
    accessor: "parent_transaction_id",
  },
  NARRATION: { label: "Narration", accessor: "remark" },
}

export type TransactionHistoryType = {
  transactions?: any[]
  columns?: any[]
  cols?: string
  colSpan?: string
  selectable?: boolean
  selectedTransactions?: any[]
  onSelect?: (value: any) => void
  onDeselect?: (value: any) => void
  labels?: Record<string, string>
}

const getPatti = (transaction: any) =>
  transaction?.trade?.id
    ? transaction?.trade
    : transaction?.receipt?.id
    ? transaction?.receipt
    : undefined

const getPaymentDate = (transaction: any) =>
  transaction?.date || transaction?.transaction_date || transaction?.created_at

const getAmount = (transaction: any) =>
  transaction?.amount || transaction?.released_amount || 0

const getPaymentMode = (transaction: any) =>
  transaction?.mode?.name ||
  transaction?.payment_mode ||
  transaction?.mode_of_payment ||
  "--"

const getAccountNumber = (transaction: any) =>
  transaction?.account_no || transaction?.account_number || "--"

const getBankName = (transaction: any) =>
  transaction?.bank?.payment_detail?.bank_name || transaction?.bank_name || "--"

type CellRenderer = (transaction: any, patti: any) => React.ReactNode

const cellRenderers: Record<string, CellRenderer> = {
  [HistoryColumns.PATTI_TYPE.accessor]: (_transaction, patti) =>
    patti?.kind ? <TradeKindTag kind={patti?.kind as any} /> : "--",
  [HistoryColumns.PATTI_NO.accessor]: (_transaction, patti) =>
    patti?.id ? `#${patti?.id}` : "--",
  [HistoryColumns.DATE.accessor]: transaction =>
    dayjs(getPaymentDate(transaction)).format("DD MMMM YYYY"),
  [HistoryColumns.ACCOUNT_NO.accessor]: transaction => (
    <div className="flex flex-col">
      <span className="font-500">{getAccountNumber(transaction)}</span>
      {getBankName(transaction) !== "--" && (
        <span className="text-[10px] text-gray-400">
          {getBankName(transaction)}
        </span>
      )}
    </div>
  ),
  [HistoryColumns.PAYMENT_MODE.accessor]: getPaymentMode,
  [HistoryColumns.ACCOUNT_HOLDER_NAME.accessor]: transaction =>
    transaction?.account_holder_name || "--",
  [HistoryColumns.ADVANCE_TYPE.accessor]: transaction =>
    transaction?.advance_payment?.parameter?.name || "--",
  [HistoryColumns.ADVANCE_AMOUNT.accessor]: transaction =>
    transaction?.advance_payment?.amount || "--",
  [HistoryColumns.PAYMENT_OPT.accessor]: transaction =>
    transaction?.advance_payment?.id ? "Advance" : "Net",
  [HistoryColumns.PARENT_TRANSACTION_ID.accessor]: transaction =>
    transaction?.parent_transaction?.transaction_id || "--",
  [HistoryColumns.NARRATION.accessor]: transaction => {
    const narration = transaction?.[HistoryColumns.NARRATION.accessor]
    return narration?.length > 15 ? (
      <Tooltip content={narration}>
        <div>{narration?.substring(0, 15) + "..."}</div>
      </Tooltip>
    ) : (
      narration || "--"
    )
  },
}

const renderCellValue = (accessor: string, transaction: any) =>
  cellRenderers?.[accessor]?.(transaction, getPatti(transaction)) ??
  transaction?.[accessor]

export const TransactionHistory = ({
  transactions,
  columns = Object.values(HistoryColumns)?.map(col => col?.accessor),
  selectable = false,
  onSelect,
  onDeselect,
  selectedTransactions,
  labels,
}: TransactionHistoryType) => {
  const [attachmentModal, setAttachmentModal] = useState(false)

  const visibleColumns = Object.values(HistoryColumns)?.filter(col =>
    columns?.includes(col?.accessor)
  )
  const columnCount = visibleColumns?.length + (selectable ? 1 : 0)
  const total = transactions?.reduce(
    (acc: number, transaction: any) => acc + (Number(transaction?.amount) || 0),
    0
  )

  const isReleasedAmount = (accessor: string) =>
    accessor === HistoryColumns.RELEASED_AMOUNT.accessor

  const renderCell = (accessor: string, transaction: any) => {
    if (isReleasedAmount(accessor)) {
      return (
        <div className="flex items-center gap-2">
          ₹{Number(getAmount(transaction)).toLocaleString()}
          {transaction?.attachments?.length > 0 && (
            <button
              type="button"
              className="bg-blue-50 text-blue-600 p-1 rounded cursor-pointer border border-blue-200"
              onClick={e => {
                e.stopPropagation()
                setAttachmentModal(transaction?.id)
              }}
            >
              <ImageIcon size={14} weight="fill" />
            </button>
          )}
        </div>
      )
    }
    return renderCellValue(accessor, transaction) || "--"
  }

  return (
    <div className="p-2 max-h-[60vh] overflow-auto">
      <table className="w-full text-[14px]">
        <thead>
          <tr className="text-gray-600">
            {selectable && (
              <th scope="col" className="text-left font-500 py-2 px-2">
                Select
              </th>
            )}
            {visibleColumns?.map(col => (
              <th
                scope="col"
                key={col?.accessor}
                className="text-left font-500 py-2 px-2"
              >
                {labels?.[col?.accessor] || col?.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {transactions?.map((transaction: any) => {
            if (!transaction?.id) return null
            return (
              <tr
                key={transaction?.id}
                className="text-gray-800 border-t border-gray-200"
              >
                {selectable && (
                  <td className="py-1.5 px-2 align-middle">
                    <Checkbox
                      checked={selectedTransactions?.includes(transaction?.id)}
                      onChange={e => {
                        if (e) {
                          onSelect?.(transaction?.id)
                        } else {
                          onDeselect?.(transaction?.id)
                        }
                      }}
                    />
                  </td>
                )}
                {visibleColumns?.map(col => (
                  <td
                    key={`value${col?.accessor}`}
                    className="py-1.5 px-2 align-middle"
                  >
                    {renderCell(col?.accessor, transaction)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr className="font-700 text-gray-800 border-t border-gray-200">
            <td colSpan={columnCount - 1} className="py-3 px-2">
              Total
            </td>
            <td className="py-3 px-2">₹{Number(total).toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>

      <AttachmentModal
        isOpen={Boolean(attachmentModal)}
        attachments={
          transactions?.find(
            (transaction: any) => transaction?.id === attachmentModal
          )?.attachments || []
        }
        previewOnly
        onClose={() => {
          setAttachmentModal(false)
        }}
      />
    </div>
  )
}

export default TransactionHistory
